import React from 'react'
import { View } from 'react-native'
import { twMerge } from 'tailwind-merge'

import { severityitemContract } from './severityitem.contract'

const VARIANTS = severityitemContract.props.variant.values
const SIZES = severityitemContract.props.size.values

const DEFAULT_SIZE = 'big'

/**
 * The props are the contract's *key set*, not the product of its two axes.
 *
 * Figma draws 13 cells, not 14: `no-data` exists only at `size=big`. Declaring
 * `variant` and `size` as two independent unions therefore let
 * `variant='no-data' size='small'` typecheck and then throw at render, which is
 * a hole the axes cannot express and only the keys can.
 */
type ContractKey = keyof typeof severityitemContract.variants

type PairOf<Key> = Key extends `variant=${infer Variant}, size=${infer Size}`
    ? { variant: Variant; size: Size }
    : never

type SeverityPair = PairOf<ContractKey>

export type SeverityItemVariant = SeverityPair['variant']
export type SeverityItemSize = SeverityPair['size']

type Shared = { className?: string; testID?: string }

// `size` is optional exactly where the default is the pair Figma draws, so
// omitting it can never select a cell that does not exist.
type PropsOf<Pair> = Pair extends { variant: infer Variant; size: infer Size }
    ? Size extends typeof DEFAULT_SIZE
        ? Shared & { variant: Variant; size?: Size }
        : Shared & { variant: Variant; size: Size }
    : never

export type SeverityItemProps = PropsOf<SeverityPair>

/**
 * The pairs Figma actually draws, in contract order.
 *
 * A caller that wants to show every severity — a legend, a story — has to
 * enumerate them, and enumerating the *axes* is what produced the hole this
 * type closes: `SEVERITY_VARIANTS × SEVERITY_SIZES` is 14 pairs and the file
 * draws 13. Deriving from the keys means the list cannot include one that
 * throws.
 */
export const SEVERITY_PAIRS: readonly SeverityPair[] = Object.keys(
    severityitemContract.variants,
).map(key => {
    const [variant, size] = key.replace('variant=', '').split(', size=')
    return { variant, size } as SeverityPair
})

const variantFor = (variant: SeverityItemVariant, size: SeverityItemSize) => {
    const key = `variant=${variant}, size=${size}`
    const found = (
        severityitemContract.variants as Record<string, { container: string; box: string }>
    )[key]
    if (!found) {
        throw new Error(`aos-ds SeverityItem: the Figma contract has no "${key}"`)
    }
    return found
}

/**
 * The status dot: a box with the dot inside it, which is two nodes and was one.
 *
 * At `big` Figma draws a 24x24 frame holding a 12x12 `status-indicator` bound to
 * `badge-round`; at `small` the frame is 8x8 and holds an ellipse that fills it.
 * The contract collapsed both into a single cell, so `big` shipped as a solid
 * 24px square in the status colour — the dot's size and its roundness were
 * nowhere. Nothing failed: the classes were all real tokens, just applied to the
 * wrong node.
 *
 * `container` is the frame and `box` is the dot. `no-data` is the one variant
 * that paints the frame itself — it is outlined rather than filled — so its
 * `box` is empty and no dot is drawn. The name is Figma's, not a mistake here.
 */
export const SeverityItem = (props: SeverityItemProps) => {
    const { variant, size = DEFAULT_SIZE, className, testID } = props
    const cell = variantFor(variant, size)
    return (
        <View
            className={twMerge('items-center justify-center', cell.container, className)}
            testID={testID ?? `severity-${variant}-${size}`}
        >
            {cell.box ? <View className={cell.box} testID='severity-dot' /> : null}
        </View>
    )
}

SeverityItem.displayName = 'SeverityItem'

export { VARIANTS as SEVERITY_VARIANTS, SIZES as SEVERITY_SIZES }
